import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnInit,
  ViewChild
} from '@angular/core';
import { DatePipe } from '@angular/common';
import {
  ChartType,
  DEFAULT_BAR_CHART_COLORS,
  DEFAULT_CHART_FONT
} from '../charts.constants';
import { ChartData } from '../models/ChartData';
import numeral from 'numeral';

declare let AmCharts: any;

@Component({
  selector: 'app-bar-chart',
  template:
    '<div #barChartCont style="width:100%" [style.height]="height"></div>',
  styleUrls: ['./bar-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BarChartComponent implements OnInit {
  @Input() data: Array<ChartData>;
  @Input() type: ChartType;
  @Input() height = '400px';
  @Input() titleArray: Array<string> = [];

  @Input() categoryField: string;
  @Input() valueFields: string[];

  @ViewChild('barChartCont', { static: true }) barChartCont: ElementRef;

  datePipe: DatePipe = new DatePipe('en-us');

  constructor(private _ngZone: NgZone) {}

  ngOnInit(): void {
    this._ngZone.runOutsideAngular(() => {
      var ivtChart = new AmCharts.AmSerialChart(AmCharts.themes['light']);
      ivtChart.categoryField = this.categoryField;
      ivtChart.theme = 'light';
      ivtChart.fontFamily = DEFAULT_CHART_FONT;
      ivtChart.colors = DEFAULT_BAR_CHART_COLORS;
      ivtChart.dataDateFormat = 'YYYY-MM';
      ivtChart.creditsPosition = 'top-right';
      var categoryAxis = ivtChart.categoryAxis;

      if (this.type === 'custom') {
        categoryAxis.labelRotation = 60;
        categoryAxis.labelOffset = 10;
        categoryAxis.gridAlpha = 0;
      } else if (this.type === 'engagement') {
        categoryAxis.labelOffset = 10;
        categoryAxis.gridAlpha = 0;
      } else {
        categoryAxis.minPeriod = 'MM';
        categoryAxis.markPeriodChange = false;
        categoryAxis.gridAlpha = 0;
        categoryAxis.labelFunction = (
          valueText,
          serialDataItem,
          categoryAxis
        ) => {
          return this.datePipe.transform(valueText, 'MMM');
        };
      }

      ivtChart.addGraph(
        this.makeGraph(this.valueFields[0], this.titleArray[0])
      );
      if (this.type === 'double' && this.valueFields[1]) {
        ivtChart.addGraph(
          this.makeGraph(this.valueFields[1], this.titleArray[1])
        );
      }
      var valueAxis = new AmCharts.ValueAxis();
      valueAxis.stackType = 'regular';
      valueAxis.gridAlpha = 0.15;
      valueAxis.axisAlpha = 0;
      valueAxis.unit = '%';
      if (this.type === 'engagement') {
        valueAxis.labelFunction = (value, valueText, axis) => {
          return this.getValue(value);
        };
      } else {
        valueAxis.labelFunction = (value, valueText, axis) => {
          return `${parseFloat((value * 100).toString()).toFixed(0)}%`;
        };
      }
      ivtChart.addValueAxis(valueAxis);
      ivtChart.removeLegend();
      ivtChart.rotate = false;
      ivtChart.dataProvider = this.data;
      ivtChart.write(this.barChartCont.nativeElement);
      this._ngZone.run(() => {
        console.debug('BAR Chart render done!');
      });
    });
  }

  makeGraph(valueField: string, title: string) {
    const amGraph = new AmCharts.AmGraph();
    amGraph.type = 'column';
    amGraph.valueField = valueField;
    amGraph.title = title;
    amGraph.fillAlphas = 1;

    if (this.type === 'engagement') {
      amGraph.balloonFunction = value => {
        return `<b>${title}</b><br><span style='font-size:14px'>${
          value.category
        }: 
                <b>${this.getValue(value.dataContext[valueField])}</b></span>`;
      };
      amGraph.fixedColumnWidth = 10;
    } else {
      amGraph.balloonFunction = value => {
        return `<b>${title}</b><br><span style='font-size:14px'>${
          value.category
        }: 
                <b>${parseFloat(
                  (value.dataContext[valueField] * 100).toString()
                ).toFixed(2)}%</b></span>`;
      };
      if (window.innerWidth > 991) {
        amGraph.fixedColumnWidth = 50;
      }
    }

    return amGraph;
  }

  getValue(value: string) {
    return numeral(value).format('0.00a').toUpperCase();
  }
}
